import type {
    NaturalArithmeticInput,
    NaturalOperation,
    PrimitiveOperationDerivationOptions,
} from '@/adapters/primitive/_contract/natural/arithmetic';
import type { PrimitiveWheelRole } from '@/adapters/primitive/_contract/operation';
import type { NaturalEvent, NaturalEventEnvelope } from '@/adapters/primitive/_contract/natural/event';
import type { NaturalReceiverInteraction } from '@/adapters/primitive/_contract/natural/location';
import type {
    NaturalBandState,
    NaturalMachineState,
    NaturalQueuedEvent,
    NaturalTrace,
    NaturalTracePoint,
} from '@/adapters/primitive/_contract/natural/machine';
import {
    digitAtPower,
    normalizeNaturalResultPowers,
    powersForValue,
    resolveCrossingSchedule,
} from '@/adapters/primitive/_pure/model/natural/columnArithmetic';
import {
    settleNaturalBandCells,
    settleNaturalBandState,
    toReceiverFrameBand,
    toTravellerFrameBand,
} from '@/adapters/primitive/_pure/model/natural/settlementCells';

export const DEFAULT_RUNAWAY_BORROW_HELPER_BUDGET = 3;

function emptyBand(power: number): NaturalBandState {
    return { power, countedSlots: [], overhangSlots: [], marker: null };
}

function wheelFromValue(args: {
    role: PrimitiveWheelRole;
    value: number;
}): NaturalMachineState['wheels'][number] {
    return {
        wheelId: args.role,
        role: args.role,
        bands: powersForValue(args.value).map((power) => ({
            power,
            countedSlots: Array.from(
                { length: digitAtPower({ value: args.value, power }) },
                (_, index) => index + 1,
            ),
            overhangSlots: [],
            marker: null,
        })),
    };
}

function valueFromWheel(wheel: NaturalMachineState['wheels'][number]): number {
    return wheel.bands.reduce(
        (total, band) => total + band.countedSlots.length * (10 ** band.power),
        0,
    );
}

function resultRoleForOperation(operation: NaturalOperation): PrimitiveWheelRole {
    switch (operation) {
        case 'addition': return 'sum';
        case 'subtraction': return 'difference';
        case 'multiplication': return 'product';
        case 'division': return 'remainder';
    }
}

function withReceiverBands(args: {
    state: NaturalMachineState;
    receiverInteraction: NaturalReceiverInteraction;
    bands: readonly NaturalBandState[];
    replaceAll?: boolean;
}): NaturalMachineState {
    const { state, receiverInteraction } = args;
    const receiver = state.wheels.find((wheel) => receiverInteraction.receiver.wheelId === undefined
        ? wheel.role === receiverInteraction.receiver.wheelRole
        : wheel.wheelId === receiverInteraction.receiver.wheelId);
    const replacements = new Map(args.bands.map((band) => [band.power, band]));
    const maxPower = Math.max(
        receiverInteraction.receiver.power,
        ...args.bands.map((band) => band.power),
    );
    const bands = args.replaceAll
        ? [...args.bands]
        : Array.from({ length: Math.max(maxPower + 1, receiver?.bands.length ?? 0) }, (_, power) => (
            replacements.get(power)
            ?? receiver?.bands.find((candidate) => candidate.power === power)
            ?? emptyBand(power)
        ));
    const wheel = {
        wheelId: receiverInteraction.receiver.wheelId ?? receiverInteraction.receiver.wheelRole,
        role: receiverInteraction.receiver.wheelRole,
        bands,
    };

    return {
        ...state,
        wheels: receiver === undefined
            ? [...state.wheels, wheel]
            : state.wheels.map((candidate) => candidate === receiver ? wheel : candidate),
    };
}

function resultInteraction(state: NaturalMachineState, power: number): NaturalReceiverInteraction {
    return {
        receiver: { wheelRole: resultRoleForOperation(state.operation), power },
        interactionKind: state.operation === 'subtraction' ? 'subtractive' : 'additive',
    };
}

function composeRadixBand(args: {
    radix: number;
    contributionTraveller: NaturalBandState;
    slotOffset: number;
}): NaturalBandState {
    return settleOntoTraveller({
        radix: args.radix,
        contributionTraveller: args.contributionTraveller,
        slotOffset: args.slotOffset,
        incoming: {
            power: args.contributionTraveller.power,
            countedSlots: Array.from({ length: args.radix }, (_, slot) => slot),
            overhangSlots: [],
            marker: null,
        },
    });
}

/** Settles a receiver-aligned incoming band onto the Contribution-traveller, which acts as the receiver. */
function settleOntoTraveller(args: {
    radix: number;
    contributionTraveller: NaturalBandState;
    slotOffset: number;
    incoming: NaturalBandState;
}): NaturalBandState {
    const settled = toTravellerFrameBand({
        band: settleNaturalBandCells({
            radix: args.radix,
            receiver: toReceiverFrameBand({ band: args.contributionTraveller, slotOffset: args.slotOffset, radix: args.radix }),
            incoming: args.incoming,
            slotOffset: 0,
        }),
        slotOffset: args.slotOffset,
        radix: args.radix,
    });
    return { ...args.contributionTraveller, countedSlots: settled.countedSlots };
}

export function createNaturalMachineState(args: {
    operation: NaturalOperation;
    radix: number;
    resultValue?: number;
    operands?: readonly [
        { role: PrimitiveWheelRole; value: number },
        { role: PrimitiveWheelRole; value: number },
    ];
}): NaturalMachineState {
    const resultRole = resultRoleForOperation(args.operation);
    return {
        operation: args.operation,
        radix: args.radix,
        nextSequence: 0,
        status: 'running',
        wheels: [
            ...(args.operands ?? []).map(wheelFromValue),
            args.resultValue === undefined
                ? { wheelId: resultRole, role: resultRole, bands: [emptyBand(0)] }
                : wheelFromValue({ role: resultRole, value: args.resultValue }),
        ],
        operandWheelTransfer: null,
        contributionTraveller: null,
        assembly: null,
        composite: null,
        multiplication: args.operation === 'multiplication'
            ? { unit: null, borrowAssembly: null }
            : null,
    };
}

export function reduceNaturalEvent(
    state: NaturalMachineState,
    event: NaturalEvent,
): NaturalMachineState {
    switch (event.eventKind) {
        case 'operand-wheel-transfer-started': {
            const sourceWheel = state.wheels.find((wheel) => event.operandWheel.wheelId === undefined
                ? wheel.role === event.operandWheel.wheelRole
                : wheel.wheelId === event.operandWheel.wheelId);
            return sourceWheel === undefined
                ? state
                : {
                    ...state,
                    operandWheelTransfer: {
                        transferId: `${sourceWheel.wheelId}-wheel`,
                        sourceRole: sourceWheel.role,
                        bands: sourceWheel.bands.map((band) => ({
                            ...band,
                            countedSlots: [...band.countedSlots],
                            overhangSlots: [...band.overhangSlots],
                        })),
                        location: {
                            receiverRole: sourceWheel.role,
                            locationKind: 'source',
                        },
                    },
                };
        }
        case 'operand-wheel-transfer-moved':
            return state.operandWheelTransfer === null
                ? state
                : {
                    ...state,
                    operandWheelTransfer: { ...state.operandWheelTransfer, location: event.location },
                };
        case 'operand-wheel-transfer-settled-on-result':
            return state.operandWheelTransfer === null
                ? state
                : {
                    ...state,
                    wheels: state.wheels.map((wheel) => wheel.role === event.receiverRole
                        ? {
                            ...wheel,
                            bands: state.operandWheelTransfer!.bands.map((band) => ({
                                ...band,
                                countedSlots: [...band.countedSlots],
                                overhangSlots: [...band.overhangSlots],
                            })),
                        }
                        : wheel),
                    operandWheelTransfer: null,
                };
        case 'unit-introduced':
            return state.multiplication === null
                ? state
                : {
                    ...state,
                    multiplication: {
                        ...state.multiplication,
                        unit: {
                            unitId: event.unitId,
                            band: { power: 0, countedSlots: [1], overhangSlots: [], marker: null },
                            slotOffset: 0,
                            location: event.location,
                            receiverInteraction: event.receiverInteraction,
                        },
                    },
                };
        case 'unit-aligned':
            return state.multiplication?.unit?.unitId !== event.unitId
                ? state
                : {
                    ...state,
                    multiplication: {
                        ...state.multiplication,
                        unit: {
                            ...state.multiplication.unit,
                            slotOffset: event.slotOffset,
                            location: event.location,
                            receiverInteraction: event.receiverInteraction,
                        },
                    },
                };
        case 'unit-crossed-zero':
            return state.multiplication?.unit?.unitId !== event.unitId
                ? state
                : {
                    ...state,
                    multiplication: {
                        ...state.multiplication,
                        unit: {
                            ...state.multiplication.unit,
                            band: {
                                ...state.multiplication.unit.band,
                                countedSlots: state.multiplication.unit.band.countedSlots.filter(
                                    (slot) => slot !== event.digit,
                                ),
                                overhangSlots: [...new Set([
                                    ...state.multiplication.unit.band.overhangSlots,
                                    event.digit,
                                ])],
                            },
                        },
                    },
                };
        case 'borrow-assembly-layer-introduced': {
            const radixBand = {
                power: event.innerPower,
                countedSlots: Array.from({ length: state.radix }, (_, slot) => slot),
                overhangSlots: [],
                marker: null,
            } satisfies NaturalBandState;
            const outerUnitBand = {
                power: event.innerPower + 1,
                countedSlots: [1],
                overhangSlots: [],
                marker: null,
            } satisfies NaturalBandState;
            const current = state.multiplication?.borrowAssembly;
            if (state.multiplication === null) return state;
            return {
                ...state,
                multiplication: {
                    ...state.multiplication,
                    borrowAssembly: current == null
                        ? {
                            assemblyId: event.assemblyId,
                            receiverInteraction: event.receiverInteraction,
                            lowerBands: [{ memberKind: 'radix-band', band: radixBand }],
                            outerUnit: { memberKind: 'next-band-unit', band: outerUnitBand, slotOffset: 0 },
                            location: event.location,
                            incomingLayer: null,
                            pendingSettledBands: [],
                        }
                        : {
                            ...current,
                            incomingLayer: { radixBand, outerUnitBand, location: event.location },
                        },
                },
            };
        }
        case 'borrow-assembly-moved': {
            const current = state.multiplication?.borrowAssembly;
            if (state.multiplication === null || current == null) return state;
            const incoming = current.incomingLayer;
            const lowerBands = incoming === null
                ? current.lowerBands.map((member) => ({
                    memberKind: 'composite' as const,
                    band: {
                        ...member.band,
                        countedSlots: Array.from({ length: state.radix - 1 }, (_, index) => index + 1),
                    },
                }))
                : [
                    ...current.lowerBands,
                    {
                        memberKind: 'composite' as const,
                        band: {
                            ...incoming.radixBand,
                            countedSlots: Array.from({ length: state.radix - 1 }, (_, index) => index + 1),
                        },
                    },
                ];
            return {
                ...state,
                multiplication: {
                    ...state.multiplication,
                    borrowAssembly: {
                        ...current,
                        lowerBands,
                        outerUnit: incoming === null
                            ? current.outerUnit
                            : { memberKind: 'next-band-unit', band: incoming.outerUnitBand, slotOffset: 0 },
                        location: event.location,
                        incomingLayer: null,
                    },
                },
            };
        }
        case 'borrow-assembly-outer-unit-aligned': {
            const current = state.multiplication?.borrowAssembly;
            if (state.multiplication === null || current == null) return state;
            return {
                ...state,
                multiplication: {
                    ...state.multiplication,
                    borrowAssembly: {
                        ...current,
                        outerUnit: { ...current.outerUnit, slotOffset: event.slotOffset },
                        location: event.location,
                    },
                },
            };
        }
        case 'borrow-assembly-outer-unit-crossed-zero': {
            const current = state.multiplication?.borrowAssembly;
            if (state.multiplication === null || current == null) return state;
            return {
                ...state,
                multiplication: {
                    ...state.multiplication,
                    borrowAssembly: {
                        ...current,
                        outerUnit: {
                            ...current.outerUnit,
                            band: {
                                ...current.outerUnit.band,
                                countedSlots: current.outerUnit.band.countedSlots.filter(
                                    (slot) => slot !== event.digit,
                                ),
                                overhangSlots: [...new Set([
                                    ...current.outerUnit.band.overhangSlots,
                                    event.digit,
                                ])],
                            },
                        },
                    },
                },
            };
        }
        case 'receiver-bands-settled': {
            const nextState = withReceiverBands({
                state,
                receiverInteraction: event.receiverInteraction,
                bands: event.bands,
                replaceAll: true,
            });
            return nextState.multiplication === null
                ? nextState
                : {
                    ...nextState,
                    multiplication: { ...nextState.multiplication, unit: null, borrowAssembly: null },
                };
        }
        case 'unit-decrement-completed':
            return state.multiplication === null
                ? state
                : {
                    ...state,
                    multiplication: { ...state.multiplication, unit: null, borrowAssembly: null },
                };
        case 'composite-formed': {
            const assembly = state.assembly;
            const composite = state.composite;
            if (assembly?.assemblyRole !== 'carry' || assembly.radixBand === null) return state;
            // The first Assembly merges with the Contribution-traveller; later ones merge onto the Composite's outer Unit.
            const merged = composite === null
                ? state.contributionTraveller === null
                    ? null
                    : { band: state.contributionTraveller.band, slotOffset: state.contributionTraveller.slotOffset }
                : { band: composite.outerUnit.band, slotOffset: composite.outerUnit.slotOffset };
            if (merged === null) return state;
            return {
                ...state,
                contributionTraveller: null,
                assembly: null,
                composite: {
                    assemblyId: composite?.assemblyId ?? event.assemblyId,
                    receiverInteraction: event.receiverInteraction,
                    lowerBands: [...(composite?.lowerBands ?? []), {
                        band: composeRadixBand({
                            radix: state.radix,
                            contributionTraveller: merged.band,
                            slotOffset: merged.slotOffset,
                        }),
                        slotOffset: merged.slotOffset,
                    }],
                    outerUnit: { band: assembly.nextBand, slotOffset: 0 },
                    location: event.location,
                },
            };
        }
        case 'composite-outer-unit-aligned': {
            const assembly = state.composite;
            if (assembly === null) return state;
            return {
                ...state,
                composite: {
                    ...assembly,
                    outerUnit: { ...assembly.outerUnit, slotOffset: event.slotOffset },
                    location: event.location,
                },
            };
        }
        case 'composite-outer-unit-crossed-zero': {
            const assembly = state.composite;
            if (assembly === null) return state;
            return {
                ...state,
                composite: {
                    ...assembly,
                    outerUnit: {
                        ...assembly.outerUnit,
                        band: {
                            ...assembly.outerUnit.band,
                            countedSlots: assembly.outerUnit.band.countedSlots.filter(
                                (slot) => slot !== event.digit,
                            ),
                            overhangSlots: [...new Set([
                                ...assembly.outerUnit.band.overhangSlots,
                                event.digit,
                            ])],
                        },
                    },
                },
            };
        }
        case 'composite-settled': {
            const nextState = withReceiverBands({
                state,
                receiverInteraction: event.receiverInteraction,
                bands: event.bands,
            });
            return { ...nextState, composite: null };
        }
        case 'contribution-traveller-taken-off': {
            const sourceBand = state.wheels
                .find((wheel) => event.contributionTraveller.wheelId === undefined
                    ? wheel.role === event.contributionTraveller.wheelRole
                    : wheel.wheelId === event.contributionTraveller.wheelId)
                ?.bands.find((band) => band.power === event.contributionTraveller.power)
                ?? emptyBand(event.contributionTraveller.power);
            return {
                ...state,
                contributionTraveller: {
                    contributionTravellerId: `${event.contributionTraveller.wheelId ?? event.contributionTraveller.wheelRole}-${event.contributionTraveller.power}`,
                    sourceRole: event.contributionTraveller.wheelRole,
                    band: {
                        ...sourceBand,
                        countedSlots: [...sourceBand.countedSlots],
                        overhangSlots: [...sourceBand.overhangSlots],
                    },
                    slotOffset: 0,
                    location: {
                        receiverRole: event.contributionTraveller.wheelRole,
                        locationKind: 'source',
                    },
                },
            };
        }
        case 'contribution-traveller-aligned':
            return state.contributionTraveller === null
                ? state
                : {
                    ...state,
                    contributionTraveller: {
                        ...state.contributionTraveller,
                        slotOffset: event.slotOffset,
                        location: event.location,
                    },
                };
        case 'contribution-cell-crossed-zero':
            return state.contributionTraveller === null
                ? state
                : {
                    ...state,
                    contributionTraveller: {
                        ...state.contributionTraveller,
                        band: {
                            ...state.contributionTraveller.band,
                            countedSlots: state.contributionTraveller.band.countedSlots.filter((slot) => slot !== event.digit),
                            overhangSlots: [...new Set([...state.contributionTraveller.band.overhangSlots, event.digit])],
                        },
                    },
                };
        case 'assembly-introduced':
            return {
                ...state,
                assembly: {
                    assemblyId: `${event.assemblyRole}-${event.innerPower}`,
                    assemblyRole: event.assemblyRole,
                    innerPower: event.innerPower,
                    radixBand: {
                        power: event.innerPower,
                        countedSlots: Array.from({ length: state.radix }, (_, slot) => slot),
                        overhangSlots: [],
                        marker: null,
                    },
                    nextBand: {
                        power: event.innerPower + 1,
                        countedSlots: event.assemblyRole === 'carry' ? [1] : [],
                        overhangSlots: [],
                        marker: event.assemblyRole === 'deficit' ? 'deficit' : null,
                    },
                    location: event.location,
                },
            };
        case 'assembly-settled-on-operand':
            return state.assembly === null
                ? state
                : {
                    ...state,
                    contributionTraveller: state.contributionTraveller === null || state.assembly.radixBand === null
                        ? state.contributionTraveller
                        : {
                            ...state.contributionTraveller,
                            band: settleOntoTraveller({
                                radix: state.radix,
                                contributionTraveller: state.contributionTraveller.band,
                                slotOffset: state.contributionTraveller.slotOffset,
                                incoming: state.assembly.radixBand,
                            }),
                        },
                    assembly: {
                        ...state.assembly,
                        radixBand: null,
                        location: event.location,
                    },
                };
        case 'contribution-traveller-settled-on-result': {
            const resultRole = resultRoleForOperation(state.operation);
            const heldBand = state.wheels
                .find((wheel) => wheel.role === resultRole)
                ?.bands.find((band) => band.power === event.contributionTraveller.power)
                ?? emptyBand(event.contributionTraveller.power);
            const contributionTraveller = state.contributionTraveller;
            const nextState = contributionTraveller === null
                ? state
                : withReceiverBands({ state, receiverInteraction: event.receiverInteraction, bands: [settleNaturalBandState({
                    radix: state.radix,
                    heldBand,
                    incomingBand: contributionTraveller.band,
                    slotOffset: contributionTraveller.slotOffset,
                })] });
            return { ...nextState, contributionTraveller: null };
        }
        case 'deficit-propagated': {
            const nextState = withReceiverBands({ state, receiverInteraction: resultInteraction(state, event.fromPower), bands: [{
                power: event.fromPower,
                countedSlots: Array.from({ length: state.radix - 1 }, (_, index) => index + 1),
                overhangSlots: [],
                marker: null,
            }] });
            return nextState.assembly === null
                ? nextState
                : {
                    ...nextState,
                    contributionTraveller: null,
                    assembly: {
                        ...nextState.assembly,
                        innerPower: event.toPower - 1,
                        radixBand: null,
                        nextBand: {
                            ...nextState.assembly.nextBand,
                            power: event.toPower,
                            marker: 'deficit',
                        },
                    },
                };
        }
        case 'deficit-discharged': {
            // The one-slot alignment lets Zero swallow one incoming cell and discharge the deficit.
            const marker = state.assembly?.nextBand;
            if (state.contributionTraveller === null || marker === undefined) return { ...state, assembly: null };
            const composite = toTravellerFrameBand({
                band: settleNaturalBandCells({
                    radix: state.radix,
                    receiver: marker,
                    incoming: state.contributionTraveller.band,
                    slotOffset: state.contributionTraveller.slotOffset,
                }),
                slotOffset: state.contributionTraveller.slotOffset,
                radix: state.radix,
            });
            return {
                ...state,
                assembly: null,
                contributionTraveller: {
                    ...state.contributionTraveller,
                    band: { ...state.contributionTraveller.band, countedSlots: composite.countedSlots },
                },
            };
        }
        case 'deficit-settlement-refused':
            // An empty band settles onto the marker without paying it, so only the contributionTraveller is consumed.
            return { ...state, contributionTraveller: null };
        case 'result-normalized': {
            const removed = new Set(event.removedPowers);
            return {
                ...state,
                wheels: state.wheels.map((wheel) => wheel.role === resultRoleForOperation(state.operation)
                    ? { ...wheel, bands: wheel.bands.filter((band) => !removed.has(band.power)) }
                    : wheel),
            };
        }
        case 'operation-completed':
            return { ...state, status: 'completed' };
        case 'operation-failed':
            return { ...state, status: 'failed' };
        case 'deficit-resolution-requested':
            return state;
    }
}

export function runNaturalEventQueue(args: {
    state: NaturalMachineState;
    queue: readonly NaturalQueuedEvent[];
}): { state: NaturalMachineState; trace: NaturalTrace } {
    const initialState = args.state;
    let state = initialState;
    const points: NaturalTracePoint[] = [];
    const queue = [...args.queue];

    while (queue.length > 0) {
        const queued = queue.shift()!;
        const envelope: NaturalEventEnvelope = {
            sequence: state.nextSequence,
            causedBySequence: queued.causedBySequence,
            event: queued.event,
        };
        const previousState = state;
        state = {
            ...reduceNaturalEvent(state, queued.event),
            nextSequence: state.nextSequence + 1,
        };
        points.push({ previousState, event: envelope, nextState: state });
    }

    return { state, trace: { initialState, points } };
}

export function deriveNaturalTrace(
    input: NaturalArithmeticInput,
    options: PrimitiveOperationDerivationOptions & { preResultInitialization?: 'transfer' | 'resident' } = {},
): NaturalTrace {
    if (input.operation === 'multiplication') {
        throw new Error('Use deriveNaturalMultiplicationTrace for multiplication.');
    }

    if (input.operation !== 'addition' && input.operation !== 'subtraction') {
        throw new Error(`Natural event derivation does not yet support ${input.operation}.`);
    }

    const isAddition = input.operation === 'addition';
    const firstRole: PrimitiveWheelRole = isAddition ? 'addend-one' : 'minuend';
    const secondRole: PrimitiveWheelRole = isAddition ? 'addend-two' : 'subtrahend';
    const resultRole: PrimitiveWheelRole = isAddition ? 'sum' : 'difference';
    const maxInputPower = Math.max(
        powersForValue(input.operandOne).length - 1,
        powersForValue(input.operandTwo).length - 1,
    );
    const transferPreResult = input.operandSchedulingMode === 'grouped'
        && options.preResultInitialization !== 'resident';
    const initialState = createNaturalMachineState({
        operation: input.operation,
        radix: input.radix,
        resultValue: input.operandSchedulingMode === 'grouped' && !transferPreResult
            ? input.operandOne
            : undefined,
        operands: [
            { role: firstRole, value: input.operandOne },
            { role: secondRole, value: input.operandTwo },
        ],
    });
    const points: NaturalTracePoint[] = [];
    let state = initialState;
    const emit = (event: NaturalEvent, causedBySequence: number | null = null): NaturalEventEnvelope => {
        const envelope: NaturalEventEnvelope = {
            sequence: state.nextSequence,
            causedBySequence,
            event,
        };
        const previousState = state;
        state = {
            ...reduceNaturalEvent(state, event),
            nextSequence: state.nextSequence + 1,
        };
        points.push({ previousState, event: envelope, nextState: state });
        return envelope;
    };
    const bandAt = (role: PrimitiveWheelRole, power: number): NaturalBandState => state.wheels
        .find((wheel) => wheel.role === role)
        ?.bands.find((band) => band.power === power)
        ?? emptyBand(power);
    const highestCountedResultPower = (): number => (state.wheels.find((wheel) => wheel.role === resultRole)
        ?.bands ?? [])
        .reduce((highest, band) => (band.countedSlots.length > 0 ? Math.max(highest, band.power) : highest), -1);
    const hasBandAt = (role: PrimitiveWheelRole, power: number): boolean => state.wheels
        .find((wheel) => wheel.role === role)
        ?.bands.some((band) => band.power === power) ?? false;
    const assemblyBudget = options.runawayBorrowAssemblyBudget ?? DEFAULT_RUNAWAY_BORROW_HELPER_BUDGET;
    // Composes the introduced carry assembly onto the contributionTraveller and settles the assembly; false once the assembly budget runs out.
    const emitUnitPropagation = (args: {
        power: number;
        receiverInteraction: NaturalReceiverInteraction;
        cause: NaturalEventEnvelope;
    }): boolean => {
        const { receiverInteraction } = args;
        const assemblyId = `${input.operation}-propagation-${args.power}`;
        let cause = emit({
            eventKind: 'composite-formed',
            assemblyId,
            location: { receiverRole: resultRole, locationKind: 'standoff-near' },
            receiverInteraction,
        }, args.cause.sequence);

        let assemblyCount = 1;
        while (state.composite !== null) {
            const assembly = state.composite;
            const outerPower = assembly.outerUnit.band.power;
            const outerHeldDigit = bandAt(resultRole, outerPower).countedSlots.length;
            const outerOffset = isAddition ? outerHeldDigit : outerHeldDigit - 1;
            cause = emit({
                eventKind: 'composite-outer-unit-aligned',
                assemblyId,
                power: outerPower,
                slotOffset: outerOffset,
                location: { receiverRole: resultRole, locationKind: 'standoff-near' },
                receiverInteraction,
            }, cause.sequence);
            const outerCrossings = resolveCrossingSchedule({
                operandDigit: 1,
                slotOffset: outerOffset,
                radix: input.radix,
            });
            for (const crossing of outerCrossings) {
                cause = emit({
                    eventKind: 'composite-outer-unit-crossed-zero',
                    assemblyId,
                    power: outerPower,
                    digit: crossing.digit,
                    receiverInteraction,
                }, cause.sequence);
            }
            if (outerCrossings.length === 0) break;
            // Above the highest counted Result power nothing can ever cancel, so the stream never ends.
            const divergent = outerPower > highestCountedResultPower();
            if (divergent && assemblyCount >= assemblyBudget) return false;
            cause = emit({
                eventKind: 'assembly-introduced',
                innerPower: outerPower,
                assemblyRole: 'carry',
                location: { receiverRole: resultRole, locationKind: 'standoff-far' },
                receiverInteraction,
            }, cause.sequence);
            cause = emit({
                eventKind: 'composite-formed',
                assemblyId,
                location: { receiverRole: resultRole, locationKind: 'standoff-near' },
                receiverInteraction,
            }, cause.sequence);
            assemblyCount += 1;
        }

        const assembly = state.composite;
        if (assembly === null) {
            throw new Error('Natural Unit propagation assembly disappeared before settlement.');
        }
        const settledBands = [...assembly.lowerBands, assembly.outerUnit].map((member) => settleNaturalBandState({
            radix: input.radix,
            heldBand: bandAt(resultRole, member.band.power),
            incomingBand: member.band,
            slotOffset: member.slotOffset,
        }));
        emit({
            eventKind: 'composite-settled',
            assemblyId,
            receiverInteraction,
            bands: settledBands,
        }, cause.sequence);
        return true;
    };

    if (input.operandSchedulingMode === 'grouped') {
        if (!isAddition && input.operandOne < input.operandTwo && !options.allowExoticTerminalStates) {
            throw new Error('Natural subtraction requires the minuend to be greater than or equal to the subtrahend.');
        }

        if (transferPreResult) {
            const takeoff = emit({
                eventKind: 'operand-wheel-transfer-started',
                operandWheel: { wheelRole: firstRole },
            });
            const moved = emit({
                eventKind: 'operand-wheel-transfer-moved',
                location: { receiverRole: resultRole, locationKind: 'receiver' },
            }, takeoff.sequence);
            emit({
                eventKind: 'operand-wheel-transfer-settled-on-result',
                receiverRole: resultRole,
            }, moved.sequence);
        }

        let runawayBorrowExhausted = false;

        for (let power = 0; power <= maxInputPower && !runawayBorrowExhausted; power += 1) {
            const secondDigit = bandAt(secondRole, power).countedSlots.length;
            // Every band the operand actually has travels, even an empty one; only absent powers are skipped.
            if (!hasBandAt(secondRole, power)) continue;

            const contributionTraveller = { wheelRole: secondRole, power };
            const receiverInteraction: NaturalReceiverInteraction = {
                receiver: { wheelRole: resultRole, power },
                interactionKind: isAddition ? 'additive' : 'subtractive',
            };
            const heldDigit = bandAt(resultRole, power).countedSlots.length;
            // An empty band has no cell to align, so it seats without turning.
            const slotOffset = secondDigit === 0
                ? 0
                : isAddition ? heldDigit : heldDigit - secondDigit;
            const takeoff = emit({ eventKind: 'contribution-traveller-taken-off', contributionTraveller });
            let cause = emit({
                eventKind: 'contribution-traveller-aligned',
                contributionTraveller,
                slotOffset,
                location: {
                    receiverRole: resultRole,
                    locationKind: slotOffset === 0 ? 'receiver' : 'standoff-near',
                },
                receiverInteraction,
            }, takeoff.sequence);
            const crossings = resolveCrossingSchedule({
                operandDigit: secondDigit,
                slotOffset,
                radix: input.radix,
            });
            for (const [index, crossing] of crossings.entries()) {
                cause = emit({
                    eventKind: 'contribution-cell-crossed-zero',
                    contributionTraveller,
                    digit: crossing.digit,
                }, cause.sequence);
                if (index === 0) {
                    cause = emit({
                        eventKind: 'assembly-introduced',
                        innerPower: power,
                        assemblyRole: 'carry',
                        location: { receiverRole: resultRole, locationKind: 'standoff-far' },
                        receiverInteraction,
                    }, cause.sequence);
                }
            }

            if (crossings.length === 0) {
                emit({
                    eventKind: 'contribution-traveller-settled-on-result',
                    contributionTraveller,
                    receiverInteraction,
                }, cause.sequence);
                continue;
            }

            if (!emitUnitPropagation({ power, receiverInteraction, cause })) runawayBorrowExhausted = true;
        }

        if (runawayBorrowExhausted) {
            emit({
                eventKind: 'operation-failed',
                failureKind: 'runaway-borrow',
                cutoff: { cutoffKind: 'assembly-budget', limit: assemblyBudget },
            });
            return { initialState, points };
        }

        const resultWheel = state.wheels.find((wheel) => wheel.role === resultRole)!;
        const normalization = normalizeNaturalResultPowers({
            powers: resultWheel.bands.map((band) => band.power),
            countedDigitByPower: Object.fromEntries(
                resultWheel.bands.map((band) => [band.power, band.countedSlots.length]),
            ),
        });
        let normalizationEvent: NaturalEventEnvelope | undefined;
        if (normalization.removedPowers.length > 0) {
            normalizationEvent = emit({
                eventKind: 'result-normalized',
                removedPowers: normalization.removedPowers,
            });
        }
        emit({ eventKind: 'operation-completed' }, normalizationEvent?.sequence ?? null);
        return { initialState, points };
    }

    for (let power = 0; power <= maxInputPower; power += 1) {
        const firstReceiverInteraction: NaturalReceiverInteraction = {
            receiver: { wheelRole: resultRole, power },
            interactionKind: 'additive',
        };
        const secondReceiverInteraction: NaturalReceiverInteraction = {
            receiver: { wheelRole: resultRole, power },
            interactionKind: isAddition ? 'additive' : 'subtractive',
        };
        const firstDigit = bandAt(firstRole, power).countedSlots.length;
        const hasDeficit = state.assembly?.assemblyRole === 'deficit'
            && state.assembly.nextBand.power === power
            && state.assembly.nextBand.marker === 'deficit';

        // As in grouped, every band the operand actually has travels, even an empty one.
        if (hasBandAt(firstRole, power) || hasDeficit) {
            emit({ eventKind: 'contribution-traveller-taken-off', contributionTraveller: { wheelRole: firstRole, power } });
            // An empty band has no cell to align, so it seats without turning.
            const firstOffset = hasDeficit ? -1 : firstDigit === 0 ? 0 : bandAt(resultRole, power).countedSlots.length;
            emit({
                eventKind: 'contribution-traveller-aligned',
                contributionTraveller: { wheelRole: firstRole, power },
                slotOffset: firstOffset,
                location: {
                    receiverRole: resultRole,
                    locationKind: hasDeficit ? 'standoff-far' : firstOffset === 0 ? 'receiver' : 'standoff-near',
                },
                receiverInteraction: firstReceiverInteraction,
            });

            if (hasDeficit) {
                const requested = emit({ eventKind: 'deficit-resolution-requested', power });
                if (firstDigit === 0) {
                    emit(
                        { eventKind: 'deficit-propagated', fromPower: power, toPower: power + 1 },
                        requested.sequence,
                    );
                } else {
                    emit({ eventKind: 'deficit-discharged', power }, requested.sequence);
                    emit({
                        eventKind: 'contribution-traveller-settled-on-result',
                        contributionTraveller: { wheelRole: firstRole, power },
                        receiverInteraction: firstReceiverInteraction,
                    });
                }
            } else {
                const crossings = isAddition ? resolveCrossingSchedule({
                    operandDigit: firstDigit,
                    slotOffset: firstOffset,
                    radix: input.radix,
                }) : [];
                let introduced: NaturalEventEnvelope | undefined;
                for (const [index, crossing] of crossings.entries()) {
                    const crossed = emit({
                        eventKind: 'contribution-cell-crossed-zero',
                        contributionTraveller: { wheelRole: firstRole, power },
                        digit: crossing.digit,
                    });
                    if (index === 0) {
                        introduced = emit({
                            eventKind: 'assembly-introduced',
                            innerPower: power,
                            assemblyRole: 'carry',
                            location: { receiverRole: resultRole, locationKind: 'standoff-far' },
                            receiverInteraction: firstReceiverInteraction,
                        }, crossed.sequence);
                    }
                }
                if (introduced === undefined) {
                    emit({
                        eventKind: 'contribution-traveller-settled-on-result',
                        contributionTraveller: { wheelRole: firstRole, power },
                        receiverInteraction: firstReceiverInteraction,
                    });
                } else {
                    // Result at the next power is still empty here, so the carry can never run away.
                    emitUnitPropagation({ power, receiverInteraction: firstReceiverInteraction, cause: introduced });
                }
            }
        }

        const secondDigit = bandAt(secondRole, power).countedSlots.length;
        if (hasBandAt(secondRole, power)) {
            const heldDigit = bandAt(resultRole, power).countedSlots.length;
            const secondOffset = secondDigit === 0 ? 0 : isAddition ? heldDigit : heldDigit - secondDigit;
            emit({ eventKind: 'contribution-traveller-taken-off', contributionTraveller: { wheelRole: secondRole, power } });
            emit({
                eventKind: 'contribution-traveller-aligned',
                contributionTraveller: { wheelRole: secondRole, power },
                slotOffset: secondOffset,
                location: {
                    receiverRole: resultRole,
                    locationKind: secondOffset === 0 ? 'receiver' : 'standoff-near',
                },
                receiverInteraction: secondReceiverInteraction,
            });
            const crossings = resolveCrossingSchedule({
                operandDigit: secondDigit,
                slotOffset: secondOffset,
                radix: input.radix,
            });
            let introduced: NaturalEventEnvelope | undefined;
            for (const [index, crossing] of crossings.entries()) {
                const crossed = emit({
                    eventKind: 'contribution-cell-crossed-zero',
                    contributionTraveller: { wheelRole: secondRole, power },
                    digit: crossing.digit,
                });
                if (index === 0) {
                    introduced = emit({
                        eventKind: 'assembly-introduced',
                        innerPower: power,
                        assemblyRole: isAddition ? 'carry' : 'deficit',
                        location: { receiverRole: resultRole, locationKind: 'standoff-far' },
                        receiverInteraction: secondReceiverInteraction,
                    }, crossed.sequence);
                }
            }
            if (isAddition && introduced !== undefined) {
                emitUnitPropagation({ power, receiverInteraction: secondReceiverInteraction, cause: introduced });
                continue;
            }
            if (introduced !== undefined) {
                emit({
                    eventKind: 'assembly-settled-on-operand',
                    innerPower: power,
                    assemblyRole: 'deficit',
                    location: { receiverRole: resultRole, locationKind: 'standoff-near' },
                    receiverInteraction: secondReceiverInteraction,
                });
            }
            emit({
                eventKind: 'contribution-traveller-settled-on-result',
                contributionTraveller: { wheelRole: secondRole, power },
                receiverInteraction: secondReceiverInteraction,
            });
        }
    }

    if (state.assembly?.assemblyRole === 'deficit') {
        if (!options.allowExoticTerminalStates) {
            throw new Error('Natural subtraction requires the minuend to be greater than or equal to the subtrahend.');
        }
        const deficitPower = state.assembly.nextBand.power;
        const deficitInteraction: NaturalReceiverInteraction = {
            receiver: { wheelRole: resultRole, power: deficitPower },
            interactionKind: 'subtractive',
        };
        const takeoff = emit({
            eventKind: 'contribution-traveller-taken-off',
            contributionTraveller: { wheelRole: firstRole, power: deficitPower },
        });
        // The zero band has nothing to turn, so it skips the far standoff and goes straight to the marker.
        const aligned = emit({
            eventKind: 'contribution-traveller-aligned',
            contributionTraveller: { wheelRole: firstRole, power: deficitPower },
            slotOffset: 0,
            location: { receiverRole: resultRole, locationKind: 'standoff-near' },
            receiverInteraction: deficitInteraction,
        }, takeoff.sequence);
        const refused = emit({
            eventKind: 'deficit-settlement-refused',
            power: deficitPower,
        }, aligned.sequence);
        emit({ eventKind: 'operation-failed', failureKind: 'unsettled-deficit' }, refused.sequence);
        return { initialState, points };
    }
    const resultWheel = state.wheels.find((wheel) => wheel.role === resultRole)!;
    const normalization = normalizeNaturalResultPowers({
        powers: resultWheel.bands.map((band) => band.power),
        countedDigitByPower: Object.fromEntries(
            resultWheel.bands.map((band) => [band.power, band.countedSlots.length]),
        ),
    });
    let normalizationEvent: NaturalEventEnvelope | undefined;
    if (normalization.removedPowers.length > 0) {
        normalizationEvent = emit({
            eventKind: 'result-normalized',
            removedPowers: normalization.removedPowers,
        });
    }
    emit({ eventKind: 'operation-completed' }, normalizationEvent?.sequence ?? null);

    return { initialState, points };
}